import { useLanguage } from '../contexts/LanguageContext';
import { 
  VehicleType, 
  TrailerType, 
  CargoType, 
  TokenTransactionType,
  PaymentMethod
} from '../types';

// Direct translation mappings to ensure consistency
const VEHICLE_TRANSLATIONS_EN: Record<string, string> = {
  "Camioneta de Carga Sencilla": "Light Cargo Pickup",
  "Camioneta de Carga con Doble Rodado": "Dual-Wheel Cargo Pickup",
  "Rabón": "Rabon",
  "Torton": "Torton",
  "Unidad en sencillo con un Remolque de 2 ejes": "Tractor with 2-Axle Trailer",
  "Unidad en sencillo con un Remolque de 3 ejes": "Tractor with 3-Axle Trailer",
  "Unidad en Full con 6 ejes en total": "Full Unit with 6 Axles",
  "Unidad en Full con 7 ejes en total": "Full Unit with 7 Axles",
  "Unidad en Full con 8 ejes en total": "Full Unit with 8 Axles",
  "Unidad en Full con 9 ejes en total": "Full Unit with 9 Axles"
};

const TRAILER_TRANSLATIONS_EN: Record<string, string> = {
  "Caja Seca": "Dry Box",
  "Caja Refrigerada": "Refrigerated Box",
  "Plataforma": "Platform",
  "Porta Contenedor": "Container Carrier",
  "Jaula Granelera": "Grain Cage",
  "Tolva Granelera": "Grain Hopper",
  "Tolva Presurizada": "Pressurized Hopper",
  "Tanque de Líquidos": "Liquid Tank",
  "LowBoy o Cama Baja": "Lowboy"
};

const CARGO_TRANSLATIONS_EN: Record<string, string> = {
  "Mercancía General": "General Merchandise",
  "Alimentos Perecederos": "Perishable Food",
  "Alimentos No Perecederos": "Non-Perishable Food",
  "Materiales de Construcción": "Construction Materials",
  "Productos Agrícolas": "Agricultural Products",
  "Mobiliario": "Furniture",
  "Ropa y Textiles": "Clothing and Textiles",
  "Productos Electrónicos": "Electronics",
  "Partes Automotrices": "Automotive Parts",
  "Electrodomésticos": "Household Appliances",
  "Acero y Metales": "Steel and Metals",
  "Productos Químicos": "Chemicals",
  "Materiales Peligrosos": "Hazardous Materials",
  "Productos Frágiles": "Fragile Items",
  "Mercancía de Alto Valor": "High-Value Goods",
  "Maquinaria": "Machinery",
  "Ganado": "Livestock",
  "Productos Farmacéuticos": "Pharmaceuticals",
  "Productos de Madera": "Wood Products",
  "Otros": "Other"
};

// Payment method translations
const PAYMENT_METHOD_TRANSLATIONS_EN: Record<string, string> = {
  "Efectivo": "Cash",
  "Tarjeta": "Card",
  "TAG": "TAG",
  "IAVE": "IAVE"
};

// Define Spanish fallback values
const VEHICLE_TRANSLATIONS_ES: Record<string, string> = {
  "Camioneta de Carga Sencilla": "Camioneta de Carga Sencilla",
  "Camioneta de Carga con Doble Rodado": "Camioneta de Carga con Doble Rodado",
  "Rabón": "Rabón",
  "Torton": "Torton",
  "Unidad en sencillo con un Remolque de 2 ejes": "Unidad en sencillo con un Remolque de 2 ejes",
  "Unidad en sencillo con un Remolque de 3 ejes": "Unidad en sencillo con un Remolque de 3 ejes",
  "Unidad en Full con 6 ejes en total": "Unidad en Full con 6 ejes en total",
  "Unidad en Full con 7 ejes en total": "Unidad en Full con 7 ejes en total",
  "Unidad en Full con 8 ejes en total": "Unidad en Full con 8 ejes en total",
  "Unidad en Full con 9 ejes en total": "Unidad en Full con 9 ejes en total"
};

/**
 * Hook to simplify translations for enums and specific types
 * with improved fallbacks and error handling
 */
export const useTranslate = () => {
  const { t, language } = useLanguage();

  // Translate vehicle type using both direct mapping and i18next
  const translateVehicleType = (type: VehicleType): string => {
    if (!type) return '';
    
    // For English, use direct mapping which is more reliable
    if (language === 'en' && VEHICLE_TRANSLATIONS_EN[type]) {
      return VEHICLE_TRANSLATIONS_EN[type];
    }
    
    // For Spanish, use direct mapping fallback
    if (language === 'es' && VEHICLE_TRANSLATIONS_ES[type]) {
      return VEHICLE_TRANSLATIONS_ES[type];
    }
    
    // Try i18next with a properly constructed key
    const key = vehicleTypeToKey(type);
    const translationKey = `vehicles.${key}`;
    const translation = t(translationKey);
    
    // Check if translation is found (not equal to the key)
    if (translation !== translationKey) {
      return translation;
    }
    
    // Return the original if translation not found
    console.warn(`No translation found for vehicle type: "${type}" (key: "${translationKey}")`);
    return type;
  };

  // Translate trailer type using both direct mapping and i18next
  const translateTrailerType = (type: TrailerType | null): string => {
    if (!type) return t('quotationForm.noTrailer', 'Sin Remolque');
    
    // For English, use direct mapping which is more reliable
    if (language === 'en' && TRAILER_TRANSLATIONS_EN[type]) {
      return TRAILER_TRANSLATIONS_EN[type];
    }
    
    // For other languages or as fallback, try i18next
    const key = trailerTypeToKey(type);
    const translationKey = `trailers.${key}`;
    const translation = t(translationKey);
    
    // Check if translation is found (not equal to the key)
    if (translation !== translationKey) {
      return translation;
    }
    
    // Return the original if translation not found
    console.warn(`No translation found for trailer type: "${type}" (key: "${translationKey}")`);
    return type;
  };

  // Translate cargo type using both direct mapping and i18next
  const translateCargoType = (type: CargoType): string => {
    if (!type) return '';
    
    // For English, use direct mapping which is more reliable
    if (language === 'en' && CARGO_TRANSLATIONS_EN[type]) {
      return CARGO_TRANSLATIONS_EN[type];
    }
    
    // For other languages or as fallback, try i18next
    const key = type.toLowerCase()
      .replace(/ /g, '')
      .replace(/[áéíóúñ]/g, c => {
        const replacements: Record<string, string> = { 
          'á': 'a', 'é': 'e', 'í': 'i', 'ó': 'o', 'ú': 'u', 'ñ': 'n'
        };
        return replacements[c] || c;
      });
    const translationKey = `cargo.${key}`;
    const translation = t(translationKey);
    
    // Check if translation is found (not equal to the key)
    if (translation !== translationKey) {
      return translation;
    }
    
    // Return the original if translation not found
    console.warn(`No translation found for cargo type: "${type}" (key: "${translationKey}")`);
    return type;
  };

  // Translate payment method based on language
  const translatePaymentMethod = (method: PaymentMethod): string => {
    // Direct translation for English
    if (language === 'en' && PAYMENT_METHOD_TRANSLATIONS_EN[method]) {
      return PAYMENT_METHOD_TRANSLATIONS_EN[method];
    }
    
    // Use i18next for other languages
    const translationKey = `paymentMethods.${method.toLowerCase()}`;
    const translation = t(translationKey);
    
    // For TAG and IAVE, which are acronyms, keep them as-is
    if (method === PaymentMethod.TAG || method === PaymentMethod.IAVE) {
      return method;
    }
    
    // Check if translation exists
    if (translation !== translationKey) {
      return translation;
    }
    
    // Return original if no translation found
    return method;
  };

  // Convert vehicle type to normalized key for translation lookup
  const vehicleTypeToKey = (type: VehicleType): string => {
    if (!type) return '';
    
    // Create a normalized key by removing spaces and accents
    let key = type.toLowerCase()
      .normalize("NFD").replace(/[\u0300-\u036f]/g, "")  // Remove accents
      .replace(/ /g, '')
      .replace(/decargas/g, '')
      .replace(/encilla/g, '')
      .replace(/condoblerodado/g, '')
      .replace(/conunr/g, '')
      .replace(/conun/, '')
      .replace(/demolque/g, '')
      .replace(/remolquede/g, 'remolque')
      .replace(/ejes/g, '')
      .replace(/entotal/g, '');
    
    return key;
  };

  // Convert trailer type to normalized key for translation lookup
  const trailerTypeToKey = (type: TrailerType): string => {
    if (!type) return '';
    
    // Create a normalized key by removing spaces and accents
    let key = type.toLowerCase()
      .normalize("NFD").replace(/[\u0300-\u036f]/g, "")  // Remove accents
      .replace(/ /g, '')
      .replace(/ó/g, 'o')
      .replace(/í/g, 'i')
      .replace(/é/g, 'e')
      .replace(/á/g, 'a')
      .replace(/ú/g, 'u')
      .replace(/lowboyocamabaja/g, 'lowboy')
      .replace(/deliquidos/g, 'liquidos')
      .replace(/cajarefrigerada/g, 'cajarefrigerada')
      .replace(/portacontenedor/g, 'portacontenedor')
      .replace(/jaulagranelera/g, 'jaulagranelera')
      .replace(/tolvagranelera/g, 'tolvagranelera')
      .replace(/tolvapresurizada/g, 'tolvapresurizada')
      .replace(/tanquedeliquidos/g, 'tanqueliquidos');
    
    return key;
  };

  // Translate transaction type
  const translateTransactionType = (type: TokenTransactionType): string => {
    const translationKey = `history.transactionTypes.${type}`;
    const translation = t(translationKey);
    return translation !== translationKey ? translation : type;
  };

  return {
    translateVehicleType,
    translateTrailerType,
    translateCargoType,
    translateTransactionType,
    translatePaymentMethod,
    // Export helper functions for other components to use
    vehicleTypeToKey,
    trailerTypeToKey
  };
};

export default useTranslate;